import { useEffect, useState } from "react";
import { Star, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Medal } from "@/components/badges/Medal";

export const StarsBar = () => {
  const { t } = useLang();
  const b = t.badges;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/my/stars").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const stars = data.stars ?? 0;
  const th = Array.isArray(data.thresholds) ? data.thresholds : [10, 30, 60];
  const level = data.level ?? 0;
  const next = th[level];
  const prev = level ? th[level - 1] : 0;
  const pct = next ? ((stars - prev) / (next - prev)) * 100 : 100;

  return (
    <section className="mt-6 bg-white border border-[#E5DCCB] px-4 sm:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="stars-bar">
      <div className="flex items-center gap-3 shrink-0">
        <span className="w-11 h-11 rounded-full bg-[#FEF3C7] grid place-items-center"><Star className="w-6 h-6 fill-[#F59E0B] text-[#F59E0B]" /></span>
        <div>
          <p className="text-xs text-stone-600">{b.title}</p>
          <p className="font-display text-2xl font-bold leading-tight" data-testid="stars-bar-total">{b.stars(stars)}</p>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="h-2 bg-[#E5DCCB] rounded-full overflow-hidden">
          <div className="h-full bg-[#F59E0B] transition-[width] duration-700" style={{ width: `${Math.max(0, Math.min(pct, 100))}%` }} />
        </div>
        <p className="text-xs text-stone-700 mt-1.5" data-testid="stars-bar-next">
          {next ? b.next(next - stars, b.names[level]) : b.all}{!stars ? ` · ${b.hint}` : ""}
        </p>
        {level >= 3 && (
          <Link to="/certificate" data-testid="stars-bar-certificate-link"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#B7791F] text-white text-xs font-semibold px-4 py-2 hover:bg-[#8C5A12] transition-colors">
            <Printer className="w-4 h-4" /> {t.cert.btn}
          </Link>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {th.map((n, i) => (
          <div key={n} className="flex flex-col items-center gap-1">
            <Medal i={i} earned={i < level} size={36} testId={`stars-bar-medal-${i}`} />
            <span className="text-[11px] text-stone-700">{b.short[i]} · {n}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
